import { useState } from 'react';
import type { SalesFunnelStage } from '@/types/sales-performance.types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { count } from './format';
import { PerformanceBarList } from './performance-bar-list';
import { PerformanceSankey } from './performance-sankey';
import { CONVERSATION_SANKEY, NUDGE_SANKEY } from './sankey-layout';

/**
 * A funnel section (Figma Kim-AI 6631:79543 conversation, 6701:27778 nudges): its
 * header with the chart-type switch, then one card holding the legend and the
 * funnel as either a Sankey or a bar list.
 */

type ChartView = 'funnel' | 'bar';
const VIEWS: { value: ChartView; label: string }[] = [{ value: 'funnel', label: 'Funnel graph' }, { value: 'bar', label: 'Bar graph' }];
const isChartView = (value: string): value is ChartView => VIEWS.some((view) => view.value === value);

const LEGEND = [
  { label: 'Moved to next step', swatch: 'bg-indigo-500' },
  { label: 'Dropped off', swatch: 'bg-gray-500' },
  { label: 'Biggest dropped off', swatch: 'bg-red-500' },
];

// The same pill select as the analytics charts' bucket picker (GranularitySelector).
function ChartTypeMenu({ title, view, onChange }: { title: string; view: ChartView; onChange: (view: ChartView) => void }) {
  return <Select value={view} onValueChange={(next) => { if (isChartView(next)) onChange(next); }}>
    <SelectTrigger
      size='sm'
      aria-label={`${title} chart type`}
      className='w-auto gap-2 rounded-full border border-gray-500 bg-white py-1 pr-2.5 pl-3 text-[13px] leading-[18px] font-normal focus:ring-0 focus:ring-offset-0 h-[28px]!'
    >
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {VIEWS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
    </SelectContent>
  </Select>;
}

interface PerformanceFunnelProps {
  stages: SalesFunnelStage[];
  title: string;
  /** The conversation funnel opens on buying intent; its first stage (all sales chats) is the cohort, not a column. */
  conversationEntry?: boolean;
}

export function PerformanceFunnel({ stages, title, conversationEntry = false }: PerformanceFunnelProps) {
  const [view, setView] = useState<ChartView>('funnel');
  const bar = view === 'bar';
  return <section aria-label={title} className='flex w-full flex-col gap-4'>
    <div className='flex w-full items-center gap-2 overflow-clip'>
      <h2 className='min-w-0 flex-1 text-xs leading-[15px] font-medium text-gray-800'>{title}</h2>
      <ChartTypeMenu title={title} view={view} onChange={setView} />
    </div>
    <div className={`flex w-full flex-col overflow-clip border border-gray-500 bg-white ${bar ? 'gap-7 rounded-[28px] px-6 pt-6 pb-4' : 'gap-6 rounded-3xl p-6'}`}>
      <div className='flex items-center gap-4 overflow-clip'>
        {LEGEND.map((item) => <span key={item.label} className='flex shrink-0 items-center gap-1.5'>
          <i className={`h-2.5 w-[17px] rounded-full ${item.swatch}`} />
          <span className='text-xs leading-[15px] whitespace-nowrap text-gray-900'>{item.label}</span>
        </span>)}
      </div>
      {bar
        ? <PerformanceBarList stages={stages} />
        : <PerformanceSankey stages={stages} title={title} hideEntry={conversationEntry} variant={conversationEntry ? CONVERSATION_SANKEY : NUDGE_SANKEY} />}
      <details className='sr-only focus-within:not-sr-only focus-within:text-xs focus-within:text-gray-800'>
        <summary className='w-fit cursor-pointer text-[11px] text-gray-700'>View counts and drop-offs</summary>
        <table className='mt-2 w-full text-left'><thead><tr><th>Step</th><th>Conversations</th><th>Drop-off from previous step</th></tr></thead>
          <tbody>{stages.map((stage) => <tr key={stage.key}><td className='py-1'>{stage.label}</td><td>{count(stage.count)}</td><td>{stage.dropOffLabel ? `${stage.dropOffLabel}: ${count(stage.dropOff)}` : '—'}</td></tr>)}</tbody>
        </table>
      </details>
    </div>
  </section>;
}
